import { createContext, useCallback, useContext, useEffect, useState } from "react";
import type { ReactNode } from "react";

interface ModalCtx {
  openCount: number;
  inc: () => void;
  dec: () => void;
}

const ModalContext = createContext<ModalCtx>({ openCount: 0, inc: () => {}, dec: () => {} });

export function ModalProvider({ children }: { children: ReactNode }) {
  const [openCount, setOpenCount] = useState(0);
  const inc = useCallback(() => setOpenCount(c => c + 1), []);
  const dec = useCallback(() => setOpenCount(c => Math.max(0, c - 1)), []);
  return (
    <ModalContext.Provider value={{ openCount, inc, dec }}>
      {children}
    </ModalContext.Provider>
  );
}

export function useModalContext() {
  return useContext(ModalContext);
}

/**
 * Call at the top of any component that owns a modal, passing the open boolean.
 * When `isOpen` is true the global modal counter increments; when it goes false
 * or the component unmounts the counter decrements. AdminBanner and
 * AdminHelpWidget use this counter to hide themselves whenever any modal is open.
 */
export function useModalOpen(isOpen: boolean) {
  const { inc, dec } = useModalContext();
  useEffect(() => {
    if (!isOpen) return;
    inc();
    return dec;
  }, [isOpen, inc, dec]);
}
